<template>
  <v-app class="app-wrapper">
    <!-- Desktop / tablet: persistent left sidebar (replaces the top app bar). Collapses to an
         icon rail; the choice is remembered per browser. -->
    <v-navigation-drawer v-if="isDesktop" permanent :width="drawerWidth" :rail="collapsed" :rail-width="72"
      color="#141a1f" class="side-drawer">
      <SideNav :collapsed="collapsed" @toggle-collapse="toggleCollapsed" />
    </v-navigation-drawer>

    <!-- Main content. v-main supplies the drawer offset via its own padding, so the inner scroll
         container (not v-main) carries our padding/background/scroll. -->
    <v-main class="app-main">
      <div class="scrollable-body" :class="{ 'has-tabbar': showMobileNav }">
        <router-view />
      </div>
    </v-main>

    <!-- Mobile: bottom tab bar (hidden on immersive screens like the 3D viewer). -->
    <MobileTabBar v-if="showMobileNav" :pending-count="breakdown.pending" />

    <!-- Phone-only: the app is designed portrait, so ask to rotate back in landscape.
         (CSS-gated to short landscape viewports = phones; tablets/desktop are unaffected.) -->
    <div class="rotate-lock">
      <v-icon size="44" color="#64ffda">mdi-phone-rotate-portrait</v-icon>
      <p class="rotate-lock-title">Rotate your device</p>
      <p class="rotate-lock-sub">Duallo works best in portrait on phones.</p>
    </div>

    <!-- App-wide confirm dialog host — the single in-app replacement for window.confirm().
         Driven by the useConfirm() service; any code can `await confirm({...})`. -->
    <ConfirmDialog :model-value="confirmState.visible" :title="confirmState.options.title"
      :message="confirmState.options.message" :confirm-text="confirmState.options.confirmText"
      :cancel-text="confirmState.options.cancelText" :danger="confirmState.options.danger"
      :icon="confirmState.options.icon" @confirm="settleConfirm(true)"
      @update:model-value="v => { if (!v) settleConfirm(false); }" />

    <!-- "All uploads complete" toast (fired by the upload manager when the queue drains cleanly). -->
    <v-snackbar v-model="uploadToast" :timeout="4000" location="bottom left" color="#14532d">
      <div class="upload-toast"><v-icon size="18">mdi-cloud-check-outline</v-icon><span>All uploads complete</span></div>
    </v-snackbar>
  </v-app>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { useRoute } from 'vue-router';
import SideNav from './components/SideNav.vue';
import MobileTabBar from './components/MobileTabBar.vue';
import ConfirmDialog from './components/dialog/ConfirmDialog.vue';
import { useConfirm } from './composable/useConfirm';
import { useAnomalyBreakdown } from './composable/useAnomalyBreakdown';
import { useUploadStore } from './store/uploadStore';

const COLLAPSE_KEY = 'duallo.sidenav.collapsed';
// Screens that take over the whole surface on mobile — no bottom tab bar.
const IMMERSIVE_ROUTES = ['AssetViewer', 'ModelVersionScans', 'ProvenanceReview'];

export default {
  name: 'App',
  components: {
    SideNav,
    MobileTabBar,
    ConfirmDialog,
  },
  setup() {
    const { mdAndUp, width } = useDisplay();
    const route = useRoute();
    const { confirmState, settleConfirm } = useConfirm();
    // Fluid drawer width: ~224px on narrow (laptop / zoomed) up to ~264px on wide displays.
    const drawerWidth = computed(() =>
      Math.round(Math.max(224, Math.min(264, 224 + 0.0625 * ((width.value || 1440) - 1280))))
    );

    // Sidebar collapse (icon rail) — persisted per browser so it stays how the user left it.
    const readCollapsed = () => {
      try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
    };
    const collapsed = ref(readCollapsed());
    const toggleCollapsed = () => {
      collapsed.value = !collapsed.value;
      try { localStorage.setItem(COLLAPSE_KEY, collapsed.value ? '1' : '0'); } catch { /* best-effort */ }
    };

    // Mobile bottom tab bar shows on the main phone screens, not on immersive ones.
    const showMobileNav = computed(() => !mdAndUp.value && !IMMERSIVE_ROUTES.includes(route.name));

    // Findings pending count for the mobile tab-bar dot. On desktop the SideNav owns this fetch, so
    // only load here when the tab bar is the active chrome (avoids a duplicate call on desktop).
    const { breakdown, load: loadAnomalyBreakdown } = useAnomalyBreakdown();
    onMounted(() => { if (!mdAndUp.value) loadAnomalyBreakdown(); });

    // "All uploads complete" toast — fired when the background upload queue drains cleanly.
    const uploadStore = useUploadStore();
    const uploadToast = ref(false);
    watch(() => uploadStore.allCompleteToken, () => { uploadToast.value = true; });

    return { isDesktop: mdAndUp, drawerWidth, collapsed, toggleCollapsed, showMobileNav, breakdown, confirmState, settleConfirm, uploadToast };
  },
};
</script>

<style scoped>
#app {
  margin: 0;
  padding: 0;
  min-height: 100vh;
  min-height: 100dvh;
  overflow: hidden;
}

.app-wrapper {
  min-height: 100vh;
  min-height: 100dvh;
  overflow: hidden;
  box-sizing: border-box;
}

.v-application--wrap {
  height: 100%;
}

.side-drawer {
  border-right: 1px solid rgba(255, 255, 255, 0.06);
}

/* Fill the viewport; v-main's own padding already offsets for the drawer (left) and app bar (top),
   so with border-box the content box is exactly the space beside/below the chrome. */
.app-main {
  height: 100vh;
  height: 100dvh;
  box-sizing: border-box;
}

/* The actual scroll container (router scrollBehavior targets this). Fills v-main's content box. */
.scrollable-body {
  height: 100%;
  overflow-y: auto;
  padding: 20px;
  box-sizing: border-box;
  background-color: #1a1f24;
  background-image: radial-gradient(120% 80% at 50% 0%, #20272e 0%, #1a1f24 55%);
  /* Match the asset-list scrollbar site-wide (thin, dark thumb, teal on hover). */
  scrollbar-width: thin;
  scrollbar-color: #3a4a52 transparent;
}

/* Non-immersive mobile: clear the notch at the top and the bottom tab bar. (Immersive screens like
   the 3D viewer have no tab bar and stay full-bleed.) */
.scrollable-body.has-tabbar {
  padding-top: calc(20px + env(safe-area-inset-top, 0px));
  padding-bottom: calc(84px + env(safe-area-inset-bottom, 0px));
}

/* Rotate-to-portrait guard — phones only. The `pointer: coarse` + `hover: none` gate limits it to
   touch devices, so a short *desktop* window never triggers it; the short-landscape size check then
   picks out phones (tablets are tall even in landscape, so they keep both orientations). */
.rotate-lock { display: none; }
@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse) and (hover: none) {
  .rotate-lock {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: #141a1f;
    color: #eef3f2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
    padding: 24px;
  }
  .rotate-lock-title { font-size: 1.1rem; font-weight: 700; margin: 6px 0 0; }
  .rotate-lock-sub { font-size: 0.9rem; color: #8aa0aa; margin: 0; }
}

.scrollable-body::-webkit-scrollbar { width: 6px; }
.scrollable-body::-webkit-scrollbar-track { background: transparent; }
.scrollable-body::-webkit-scrollbar-thumb { background: #3a4a52; border-radius: 3px; }
.scrollable-body::-webkit-scrollbar-thumb:hover { background: var(--duallo-accent); }

.upload-toast { display: inline-flex; align-items: center; gap: 8px; font-weight: 600; }
</style>
